import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHECKOUT_PAID_PLANS,
  PaidPlanBillingToggle,
  PaidPlanOfferingCard,
  toLandingBillingInterval,
} from '@/components/billing/paid-plan-offering';
import { SeatCountStepper } from '@/components/billing/seat-count-stepper';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { quoteCheckoutAmountMinor } from '@/lib/billing-checkout-quote';
import { cn } from '@/utils/cn';
import { formatDate, formatMoney } from '@/utils/format';

import {
  canOpenPlanCheckout,
  needsSubscriptionRegistration,
} from './subscription-checkout-access';
import type {
  AppliedCoupon,
  CheckoutPlan,
  CheckoutSession,
  PaidPlan,
  SubscriptionsSummary,
} from './subscriptions-types';
import { PAID_PLAN_RANK } from './subscriptions-types';

/** Keep in sync with `server/src/modules/billing/billing-pricing.ts` (20% yearly discount). */
const FALLBACK_CHECKOUT_PLANS: CheckoutPlan[] = [
  {
    plan: 'essential',
    includedSeats: 8,
    currency: 'GHS',
    monthlyAmountMinor: 50_00 * 8,
    annualAmountMinor: 40_00 * 8 * 12,
  },
  {
    plan: 'professional',
    includedSeats: 15,
    currency: 'GHS',
    monthlyAmountMinor: 100_00 * 15,
    annualAmountMinor: 80_00 * 15 * 12,
  },
];

type PlanAction = 'current' | 'renew' | 'upgrade' | 'downgrade' | 'subscribe';
type CheckoutAction = 'subscribe' | 'upgrade' | 'renew';

const couponDurationMessage = (
  coupon: AppliedCoupon,
  translate: (key: string, options?: { count: number }) => string,
): string => {
  if (coupon.durationType === 'forever') return translate('checkout.couponDurationForever');
  if (coupon.durationType === 'repeating' && coupon.durationMonths) {
    return translate('checkout.couponDurationMonths', { count: coupon.durationMonths });
  }
  return translate('checkout.couponDurationOnce');
};

export const AdjustPlanDialog = ({
  open,
  onOpenChange,
  summary,
  initialCouponCode = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: SubscriptionsSummary;
  initialCouponCode?: string | null;
}) => {
  const { t } = useTranslation('billing');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const [interval, setInterval] = useState<'monthly' | 'annual'>(
    summary.subscription.billingInterval === 'monthly' ? 'monthly' : 'annual',
  );
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [couponContext, setCouponContext] = useState<{
    plan: PaidPlan;
    interval: 'monthly' | 'annual';
  } | null>(null);
  const [downgradeTarget, setDowngradeTarget] = useState<PaidPlan | null>(null);
  const [seatCountsByPlan, setSeatCountsByPlan] = useState<Partial<Record<PaidPlan, number>>>({});
  const [pendingCheckout, setPendingCheckout] = useState<{
    plan: PaidPlan;
    action: CheckoutAction;
  } | null>(null);
  const autoAppliedKey = useRef<string | null>(null);

  const management = summary.management;
  const currentPlan = summary.subscription.plan;
  const canAdjustPlan = management?.canAdjustPlan ?? false;
  const needsPaidPlan = needsSubscriptionRegistration(summary);

  const canRenewCurrentPlan = management?.canRenewCurrentPlan ?? false;
  const billingInterval = summary.subscription.billingInterval ?? 'annual';

  const checkoutPlans = summary.checkout?.plans?.length
    ? summary.checkout.plans
    : FALLBACK_CHECKOUT_PLANS;
  const testCheckout = Boolean(summary.checkout?.testCheckoutAllowed);
  const paymentsConfigured = Boolean(summary.checkout?.paymentsConfigured);
  const periodEnd = summary.subscription.currentPeriodEnd;
  const landingInterval = toLandingBillingInterval(interval);
  const yearly = interval === 'annual';
  const seatsInUse = summary.subscription.seatsInUse;

  const resolvePlanAction = (plan: PaidPlan): PlanAction => {
    const currentPaidRank =
      currentPlan === 'essential' || currentPlan === 'professional'
        ? PAID_PLAN_RANK[currentPlan]
        : null;

    if (currentPlan === plan) {
      const sameInterval = interval === billingInterval;
      const overdue =
        summary.subscription.status === 'past_due' ||
        summary.subscription.status === 'grace' ||
        summary.subscription.status === 'suspended';
      if (canRenewCurrentPlan && (overdue || sameInterval)) {
        return 'renew';
      }
      if (canAdjustPlan && summary.subscription.status === 'active') return 'current';
      return 'subscribe';
    }
    if (canAdjustPlan && currentPaidRank !== null && PAID_PLAN_RANK[plan] < currentPaidRank) {
      return 'downgrade';
    }
    if (canAdjustPlan && currentPaidRank !== null && PAID_PLAN_RANK[plan] > currentPaidRank) {
      return 'upgrade';
    }
    return 'subscribe';
  };

  const visiblePlans = CHECKOUT_PAID_PLANS.filter((plan) =>
    checkoutPlans.some((checkoutPlan) => checkoutPlan.plan === plan),
  );

  const seatFloorFor = (plan: PaidPlan): number => {
    const included =
      checkoutPlans.find((checkoutPlan) => checkoutPlan.plan === plan)?.includedSeats ??
      (plan === 'essential' ? 8 : 15);
    const fromPaidPlan = currentPlan === 'essential' || currentPlan === 'professional';
    const carryTotal = fromPaidPlan ? summary.subscription.totalSeats : 0;
    return Math.max(included, seatsInUse, carryTotal);
  };

  const resolveSeatCount = (plan: PaidPlan): number => {
    const floor = seatFloorFor(plan);
    const draft = seatCountsByPlan[plan];
    return draft != null ? Math.max(draft, floor) : floor;
  };

  const allowsSeatSelection = (action: PlanAction): boolean =>
    action === 'subscribe' || action === 'upgrade';

  useEffect(() => {
    if (!open) return;
    setSeatCountsByPlan({});
    setAppliedCoupon(null);
    setCouponContext(null);
    setCouponCode(initialCouponCode?.trim().toUpperCase() ?? '');
    setDowngradeTarget(null);
    setPendingCheckout(null);
    autoAppliedKey.current = null;
  }, [open, initialCouponCode]);

  const invalidateBilling = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    await queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
    await queryClient.invalidateQueries({ queryKey: ['billing-receipts'] });
    await queryClient.invalidateQueries({ queryKey: ['billing-assigned-coupons'] });
  };

  const validateCoupon = useMutation({
    mutationFn: (input: { plan: PaidPlan; interval: 'monthly' | 'annual'; seatCount: number }) =>
      api.post<AppliedCoupon>('/billing/coupons/validate', {
        plan: input.plan,
        interval: input.interval,
        couponCode,
        seatCount: input.seatCount,
      }),
    onSuccess: (result, variables) => {
      setAppliedCoupon(result);
      setCouponContext({ plan: variables.plan, interval: variables.interval });
      toast.success(t('checkout.couponApplied'));
    },
    onError: (err) => {
      setAppliedCoupon(null);
      setCouponContext(null);
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const checkout = useMutation({
    mutationFn: (input: {
      plan: PaidPlan;
      interval: 'monthly' | 'annual';
      includeSeatCount: boolean;
    }) =>
      api.post<CheckoutSession>('/billing/checkout-sessions', {
        plan: input.plan,
        interval: input.interval,
        ...(input.includeSeatCount ? { seatCount: resolveSeatCount(input.plan) } : {}),
        couponCode:
          appliedCoupon &&
          couponContext?.plan === input.plan &&
          couponContext.interval === input.interval
            ? appliedCoupon.code
            : couponCode.trim() || undefined,
      }),
    onSuccess: async (session) => {
      setPendingCheckout(null);
      if (session.status === 'paid') {
        await invalidateBilling();
        onOpenChange(false);
        toast.success(session.testMode ? t('checkout.testSuccess') : t('checkout.returnSuccess'));
        return;
      }
      if (session.checkoutUrl) {
        window.location.assign(session.checkoutUrl);
        return;
      }
      if (session.status === 'processing' || session.status === 'pending') {
        await invalidateBilling();
        onOpenChange(false);
        toast.info(t('checkout.pending'));
        return;
      }
      toast.error(t('checkout.failed'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const scheduleDowngrade = useMutation({
    mutationFn: (input: { plan: PaidPlan; interval: 'monthly' | 'annual' }) =>
      api.post('/billing/subscription/schedule-downgrade', input),
    onSuccess: async () => {
      setDowngradeTarget(null);
      await invalidateBilling();
      onOpenChange(false);
      toast.success(t('management.downgradeScheduled'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const planButtonLabel = (action: PlanAction): string => {
    if (action === 'current') return t('checkout.currentPlan');
    if (action === 'renew') return t('checkout.renew');
    if (action === 'upgrade') return t('checkout.upgrade');
    if (action === 'downgrade') return t('checkout.downgrade');
    return t('checkout.subscribe');
  };

  const toCheckoutAction = (action: PlanAction): CheckoutAction => {
    if (action === 'renew') return 'renew';
    if (action === 'upgrade') return 'upgrade';
    return 'subscribe';
  };

  const checkoutPlanMeta = (plan: PaidPlan) =>
    checkoutPlans.find((checkoutPlan) => checkoutPlan.plan === plan);

  const reviewSeatCount = (plan: PaidPlan, action: CheckoutAction): number =>
    action === 'renew' ? summary.subscription.totalSeats : resolveSeatCount(plan);

  useEffect(() => {
    if (!pendingCheckout || !couponCode.trim() || appliedCoupon) return;
    const key = `${pendingCheckout.plan}:${pendingCheckout.action}:${interval}:${couponCode}`;
    if (autoAppliedKey.current === key) return;
    autoAppliedKey.current = key;
    validateCoupon.mutate({
      plan: pendingCheckout.plan,
      interval,
      seatCount: reviewSeatCount(pendingCheckout.plan, pendingCheckout.action),
    });
    // Apply a pre-filled assigned code once when review opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCheckout]);

  const reviewSubtotalMinor = (plan: PaidPlan, action: CheckoutAction): number | null => {
    const meta = checkoutPlanMeta(plan);
    if (!meta) return null;
    return quoteCheckoutAmountMinor(meta, interval, reviewSeatCount(plan, action));
  };

  const reviewDiscountMinor = (plan: PaidPlan, subtotalMinor: number | null): number => {
    if (
      !appliedCoupon ||
      !subtotalMinor ||
      couponContext?.plan !== plan ||
      couponContext.interval !== interval
    ) {
      return 0;
    }
    return Math.min(appliedCoupon.discountMinor, subtotalMinor);
  };

  const reviewConfirmLabel = (action: CheckoutAction): string => {
    if (action === 'upgrade') return t('checkout.reviewConfirmUpgrade');
    if (action === 'renew') return t('checkout.reviewConfirmRenew');
    return t('checkout.reviewConfirmSubscribe');
  };

  const planCheckoutQuote = (plan: PaidPlan, action: PlanAction) => {
    if (action === 'downgrade' || action === 'current') return null;
    const checkoutAction = toCheckoutAction(action);
    const meta = checkoutPlanMeta(plan);
    if (!meta) return null;
    const seats = reviewSeatCount(plan, checkoutAction);
    const subtotalMinor = quoteCheckoutAmountMinor(meta, interval, seats);
    const discountMinor = reviewDiscountMinor(plan, subtotalMinor);
    return {
      meta,
      seats,
      subtotalMinor,
      discountMinor,
      totalMinor: subtotalMinor - discountMinor,
    };
  };

  const billingPeriodSuffix = interval === 'annual' ? t('checkout.perYear') : t('checkout.perMonth');

  const updateSeatDraft = (plan: PaidPlan, value: number) => {
    setSeatCountsByPlan((previous) => ({
      ...previous,
      [plan]: Math.max(value, seatFloorFor(plan)),
    }));
    setAppliedCoupon(null);
    setCouponContext(null);
  };

  if (!needsPaidPlan && !canAdjustPlan) return null;

  const pendingReview = pendingCheckout
    ? {
        ...pendingCheckout,
        meta: checkoutPlanMeta(pendingCheckout.plan),
        seats: reviewSeatCount(pendingCheckout.plan, pendingCheckout.action),
        subtotalMinor: reviewSubtotalMinor(pendingCheckout.plan, pendingCheckout.action),
      }
    : null;
  const pendingDiscountMinor =
    pendingReview?.subtotalMinor != null
      ? reviewDiscountMinor(pendingReview.plan, pendingReview.subtotalMinor)
      : 0;
  const pendingTotalMinor =
    pendingReview?.subtotalMinor != null
      ? pendingReview.subtotalMinor - pendingDiscountMinor
      : null;

  const planChoices = (
    <>
      <PaidPlanBillingToggle
        yearly={yearly}
        onYearlyChange={(nextYearly) => {
          setInterval(nextYearly ? 'annual' : 'monthly');
          setAppliedCoupon(null);
          setCouponContext(null);
        }}
      />

      {seatsInUse > 0 ? (
        <p className="text-center text-caption text-fg-secondary">
          {t('checkout.seatCountInUse', { count: seatsInUse })}
        </p>
      ) : null}

      <ul className="grid items-stretch gap-4 sm:grid-cols-2">
              {visiblePlans.map((plan) => {
                const isCurrent = currentPlan === plan;
                const action = resolvePlanAction(plan);
                const isDisabled = action === 'current';
                const isFeatured = plan === 'professional';
                const quote = planCheckoutQuote(plan, action);
                const planFloor = seatFloorFor(plan);
                const showSeats = allowsSeatSelection(action);

                return (
                  <li key={plan} className="flex min-w-0">
                    <PaidPlanOfferingCard
                      plan={plan}
                      interval={landingInterval}
                      featured={isFeatured}
                      isCurrent={isCurrent}
                      currentPlanBadge={t('plans.currentPlan')}
                      className="flex-1"
                      footer={
                        <>
                          {showSeats ? (
                            <div className="mb-3 rounded-lg border border-brand/35 bg-brand/5 p-3 ring-1 ring-brand/15">
                              <div className="flex items-center justify-between gap-3">
                                <label
                                  className="text-body-sm font-medium text-fg"
                                  htmlFor={`billing-seats-${plan}`}
                                >
                                  {t('checkout.seatCountLabel')}
                                </label>
                                <div className="flex items-center gap-2">
                                  <SeatCountStepper
                                    id={`billing-seats-${plan}`}
                                    value={resolveSeatCount(plan)}
                                    min={planFloor}
                                    decreaseLabel={t('checkout.seatDecrease')}
                                    increaseLabel={t('checkout.seatIncrease')}
                                    onChange={(value) => updateSeatDraft(plan, value)}
                                  />
                                  <span className="text-caption font-medium text-brand">
                                    {t('checkout.seatMinForPlan', { minimum: planFloor })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {quote ? (
                            <div
                              className={cn(
                                'space-y-1 text-center',
                                showSeats ? 'mb-3' : 'mb-3 border-t border-border pt-4',
                              )}
                            >
                              <p className="text-caption text-fg-secondary">
                                {t('checkout.planTotalLabel')} ·{' '}
                                {t('checkout.planTotalSeats', { count: quote.seats })}
                              </p>
                              {quote.discountMinor > 0 ? (
                                <>
                                  <p className="text-body-sm text-fg-secondary line-through">
                                    {formatMoney(quote.subtotalMinor, quote.meta.currency)}
                                    {billingPeriodSuffix}
                                  </p>
                                  <p className="text-section-title font-semibold text-brand">
                                    {formatMoney(quote.totalMinor, quote.meta.currency)}
                                    {billingPeriodSuffix}
                                  </p>
                                </>
                              ) : (
                                <p className="text-section-title font-semibold text-brand">
                                  {formatMoney(quote.totalMinor, quote.meta.currency)}
                                  {billingPeriodSuffix}
                                </p>
                              )}
                            </div>
                          ) : null}

                          <Button
                            type="button"
                            className="w-full"
                            size="lg"
                            variant={
                              action === 'downgrade' || action === 'current'
                                ? 'outline'
                                : isFeatured && !isCurrent
                                  ? 'primary'
                                  : 'outline'
                            }
                            disabled={isDisabled}
                            loading={
                              (checkout.isPending && checkout.variables?.plan === plan) ||
                              (scheduleDowngrade.isPending && downgradeTarget === plan)
                            }
                            onClick={() => {
                              if (action === 'downgrade') {
                                setDowngradeTarget(plan);
                                return;
                              }
                              if (action === 'current') return;
                              setPendingCheckout({ plan, action: toCheckoutAction(action) });
                            }}
                          >
                            {planButtonLabel(action)}
                          </Button>
                        </>
                      }
                    />
                  </li>
                );
              })}
      </ul>
    </>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="xl" closeLabel={tCommon('action.close')}>
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>{canAdjustPlan ? t('checkout.adjustTitle') : t('checkout.title')}</DialogTitle>
            <DialogDescription>
              {canAdjustPlan ? t('checkout.adjustDescription') : t('checkout.description')}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="max-h-[min(85dvh,50rem)]" contentClassName="space-y-4">
            {planChoices}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingCheckout !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingCheckout(null);
        }}
      >
        <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('checkout.reviewTitle')}</DialogTitle>
          </DialogHeader>

          {pendingReview?.meta && pendingReview.subtotalMinor != null && pendingTotalMinor != null ? (
            <DialogBody contentClassName="space-y-4">
              <dl className="space-y-2 text-body-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('checkout.reviewPlan')}</dt>
                  <dd className="font-medium text-fg">{t(`plans.${pendingReview.plan}`)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('checkout.reviewSeats')}</dt>
                  <dd className="font-medium text-fg">{pendingReview.seats}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('checkout.reviewBilling')}</dt>
                  <dd className="font-medium text-fg">
                    {interval === 'annual'
                      ? t('checkout.reviewBillingAnnual')
                      : t('checkout.reviewBillingMonthly')}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-border pt-2">
                  <dt className="text-fg-secondary">{t('checkout.reviewSubtotal')}</dt>
                  <dd className="font-medium text-fg">
                    {formatMoney(pendingReview.subtotalMinor, pendingReview.meta.currency)}
                  </dd>
                </div>
                {pendingDiscountMinor > 0 ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-fg-secondary">{t('checkout.reviewDiscount')}</dt>
                    <dd className="font-medium text-success">
                      −{formatMoney(pendingDiscountMinor, pendingReview.meta.currency)}
                    </dd>
                  </div>
                ) : null}
                <div className="flex justify-between gap-4 border-t border-border pt-2">
                  <dt className="font-medium text-fg">{t('checkout.reviewTotal')}</dt>
                  <dd className="text-section-title font-semibold text-brand">
                    {formatMoney(pendingTotalMinor, pendingReview.meta.currency)}
                  </dd>
                </div>
              </dl>

              {!appliedCoupon ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <label
                      className="mb-1 block text-caption text-fg-secondary"
                      htmlFor="billing-review-coupon"
                    >
                      {t('checkout.couponLabel')}
                    </label>
                    <Input
                      id="billing-review-coupon"
                      value={couponCode}
                      onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                      placeholder={t('checkout.couponPlaceholder')}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    loading={validateCoupon.isPending}
                    disabled={!couponCode.trim()}
                    onClick={() => {
                      if (!pendingCheckout) return;
                      validateCoupon.mutate({
                        plan: pendingCheckout.plan,
                        interval,
                        seatCount: reviewSeatCount(pendingCheckout.plan, pendingCheckout.action),
                      });
                    }}
                  >
                    {t('checkout.couponApply')}
                  </Button>
                </div>
              ) : null}

              {appliedCoupon && appliedCoupon.bonusMonths > 0 ? (
                <p className="text-body-sm text-fg-secondary">
                  {t('checkout.couponBonusMonths', { count: appliedCoupon.bonusMonths })}
                </p>
              ) : null}
              {appliedCoupon && appliedCoupon.bonusMonths === 0 ? (
                <p className="text-body-sm text-fg-secondary">
                  {couponDurationMessage(appliedCoupon, t)}
                </p>
              ) : null}
              {testCheckout && !paymentsConfigured ? (
                <p className="text-caption text-fg-secondary">{t('checkout.reviewTestNote')}</p>
              ) : null}
            </DialogBody>
          ) : (
            <DialogBody>
              <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
            </DialogBody>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingCheckout(null)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              loading={checkout.isPending}
              disabled={!pendingReview?.meta || pendingTotalMinor == null}
              onClick={() => {
                if (!pendingCheckout) return;
                checkout.mutate({
                  plan: pendingCheckout.plan,
                  interval,
                  includeSeatCount:
                    pendingCheckout.action === 'subscribe' ||
                    pendingCheckout.action === 'upgrade',
                });
              }}
            >
              {pendingCheckout ? reviewConfirmLabel(pendingCheckout.action) : t('checkout.subscribe')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={downgradeTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDowngradeTarget(null);
        }}
        title={t('management.downgradeConfirmTitle')}
        description={t('management.downgradeConfirmDescription', {
          plan: downgradeTarget ? t(`plans.${downgradeTarget}`) : '',
          date: periodEnd ? formatDate(periodEnd) : '',
        })}
        confirmLabel={t('checkout.downgrade')}
        cancelLabel={tCommon('action.cancel')}
        loading={scheduleDowngrade.isPending}
        onConfirm={() => {
          if (!downgradeTarget) return;
          scheduleDowngrade.mutate({ plan: downgradeTarget, interval });
        }}
      />
    </>
  );
};

export const canShowAdjustPlanDialog = canOpenPlanCheckout;
